import { supabase, type UserProfile } from './supabase';

/**
 * Authentication Service
 *
 * Handles login, OTP verification, and user profile operations
 * with proper RLS handling in the public schema.
 * Session management is handled by Supabase automatically.
 */

export interface LoginResult {
  success: boolean;
  error?: string;
  user?: UserProfile;
}

export const authService = {
  /**
   * Check if user exists in database
   */
  async checkUserExists(
    phone: string
  ): Promise<{ exists: boolean; user?: UserProfile; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('ev_user_profiles')
        .select('*')
        .eq('phone', phone.trim())
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        return {
          exists: false,
          error: `Database error: ${error.message}`,
        };
      }

      return {
        exists: !!data,
        user: data || undefined,
      };
    } catch (err) {
      return {
        exists: false,
        error: `Unexpected error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Send OTP to phone number
   */
  async sendOtp(phone: string): Promise<{ success: boolean; error?: string }> {
    try {
      // First check if user exists in database
      const userCheck = await this.checkUserExists(phone);
      if (!userCheck.exists) {
        return {
          success: false,
          error:
            'You are not registered with EyeVantage. Please contact your administrator.',
        };
      }

      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: {
          shouldCreateUser: true, // Allow creating users if they exist in ev_user_profiles
        },
      });

      if (error) {
        // Handle SMS provider errors
        if (
          error.message.includes('SMS provider') ||
          error.message.includes('Unable to get SMS provider')
        ) {
          return {
            success: false,
            error:
              'SMS provider not configured. Please contact your administrator.',
          };
        }

        // Handle rate limiting
        if (error.message.includes('rate limit')) {
          return {
            success: false,
            error:
              'Rate limit exceeded. Please wait before requesting another OTP.',
          };
        }

        // Handle invalid phone
        if (error.message.includes('invalid phone')) {
          return {
            success: false,
            error:
              'Please enter a valid phone number with country code (e.g., +1234567890).',
          };
        }

        return {
          success: false,
          error: error.message,
        };
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: `Unexpected error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Verify OTP and get user profile
   */
  async verifyOtp(phone: string, otp: string): Promise<LoginResult> {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone,
        token: otp,
        type: 'sms',
      });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      if (!data.user) {
        return {
          success: false,
          error: 'No user returned from verification',
        };
      }

      // Get user profile from database
      const { data: profile, error: profileError } = await supabase
        .from('ev_user_profiles')
        .select('*')
        .eq('phone', phone.trim())
        .eq('is_active', true)
        .single();

      if (profileError) {
        return {
          success: false,
          error: `Profile error: ${profileError.message}`,
        };
      }

      if (!profile) {
        return {
          success: false,
          error: 'User profile not found',
        };
      }

      // Link the auth user to the existing profile if not already linked
      if (!profile.auth_user_id && data.user) {
        const { error: updateError } = await supabase
          .from('ev_user_profiles')
          .update({
            auth_user_id: data.user.id,
            last_login: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', profile.id);

        if (updateError) {
          console.warn('Failed to link auth user to profile:', updateError);
          // Don't fail the login, just log the warning
        } else {
          // Update the profile object with the new auth_user_id
          profile.auth_user_id = data.user.id;
          profile.last_login = new Date().toISOString();
          profile.updated_at = new Date().toISOString();
        }
      }

      return {
        success: true,
        user: profile,
      };
    } catch (err) {
      return {
        success: false,
        error: `Unexpected error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Get current session (delegated to Supabase)
   */
  async getCurrentSession() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    return { session, error };
  },

  /**
   * Sign out (delegated to Supabase)
   */
  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },
};
